"use client";

import type React from "react";
import { createContext, useContext } from "react";
import { ArrowUpRight, CalendarDays, Copy, Mail } from "lucide-react";
import { resolveProfileLink } from "@/lib/link-types";
import type { MessageKey } from "@/lib/i18n";
import { analyzeThemeSection } from "@/lib/theme-structure";
import type { MarkdownSection, ProfileLink, ProfileRenderModel } from "@/lib/types";
import { MarkdownBody, MarkdownInline, MarkdownInlineText } from "../MarkdownBody";

export type ThemeRendererProps = {
  model: ProfileRenderModel;
  interactiveLinks: boolean;
  showAttribution: boolean;
  onLinkClick?: (link: ProfileLink) => void;
  t: (key: MessageKey, params?: Record<string, string | number | boolean | null | undefined>) => string;
};

const InteractiveLinkBasePathContext = createContext<string | undefined>(undefined);

export function InteractiveLinkBasePathProvider({
  basePath,
  children
}: {
  basePath?: string;
  children: React.ReactNode;
}) {
  return <InteractiveLinkBasePathContext.Provider value={basePath}>{children}</InteractiveLinkBasePathContext.Provider>;
}

export function LinkActionIcon({
  action,
  link
}: {
  action: ReturnType<typeof resolveProfileLink>["action"];
  link: ProfileLink;
}) {
  if (link.type === "calendar") return <CalendarDays size={14} />;
  if (action === "copy") return <Copy size={14} />;
  if (action === "mailto") return <Mail size={14} />;
  return <ArrowUpRight size={14} />;
}

export function ProfileLinkItem({
  link,
  interactiveLinks,
  onLinkClick,
  className,
  children
}: {
  link: ProfileLink;
  interactiveLinks: boolean;
  onLinkClick?: (link: ProfileLink) => void;
  className: string;
  children: React.ReactNode;
}) {
  const resolved = resolveProfileLink(link);
  const fullClassName = `${className} action-${resolved.action}`;
  const redirectBasePath = useContext(InteractiveLinkBasePathContext);
  const interactiveHref = resolved.href && redirectBasePath
    ? `${redirectBasePath.replace(/\/$/, "")}/${encodeURIComponent(link.id)}`
    : resolved.href;

  if (interactiveLinks && interactiveHref) {
    return (
      <a
        className={fullClassName}
        href={interactiveHref}
        onClick={(event) => {
          if (!onLinkClick) return;
          event.preventDefault();
          onLinkClick(link);
        }}
      >
        {children}
      </a>
    );
  }

  if (interactiveLinks) {
    return (
      <button type="button" className={fullClassName} onClick={() => onLinkClick?.(link)}>
        {children}
      </button>
    );
  }

  if (resolved.href) {
    return (
      <a className={fullClassName} href={resolved.href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  }

  return <span className={fullClassName}>{children}</span>;
}

export function RenderSections({ sections, className }: { sections: MarkdownSection[]; className?: string }) {
  if (sections.length === 0) return null;
  return (
    <div className={className}>
      {sections.map((section) => (
        <section key={section.id} className="v2-section">
          {section.kind !== "default" && <h2>{section.title}</h2>}
          <MarkdownBody nodes={section.nodes} />
        </section>
      ))}
    </div>
  );
}

export function ThemeSectionBody({
  section,
  prefix,
  proseClassName,
  entryBadge,
  entryLeader = false,
  showEntryHrefPreview = false
}: {
  section: MarkdownSection;
  prefix: string;
  proseClassName?: string;
  entryBadge?: (index: number) => string;
  entryLeader?: boolean;
  showEntryHrefPreview?: boolean;
}) {
  const layout = analyzeThemeSection(section);
  if (layout.type === "prose") {
    return <MarkdownBody nodes={layout.nodes} className={proseClassName} />;
  }

  const wrapperClassName = proseClassName
    ? `${prefix}-structured ${proseClassName}`
    : `${prefix}-structured`;

  if (layout.type === "timeline") {
    return (
      <div className={wrapperClassName} data-layout="timeline">
        {layout.lead.length > 0 && <MarkdownBody nodes={layout.lead} className={`${prefix}-lead`} />}
        <ol className={`${prefix}-timeline`}>
          {layout.items.map((item, index) => (
            <li className={`${prefix}-timeline-item`} key={index}>
              <span className={`${prefix}-period`} aria-hidden={item.period ? undefined : true}>
                {item.period || String(index + 1).padStart(2, "0")}
              </span>
              <div className={`${prefix}-timeline-body`}>
                <p><MarkdownInline nodes={item.body} keyPrefix={`${prefix}-t-${index}`} /></p>
                {item.extra.length > 0 && <MarkdownBody nodes={item.extra} />}
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} data-layout="entries">
      {layout.lead.length > 0 && <MarkdownBody nodes={layout.lead} className={`${prefix}-lead`} />}
      <ul className={`${prefix}-entries`} data-count={layout.entries.length}>
        {layout.entries.map((entry, index) => {
          const badge = entryBadge ? entryBadge(index) : String(index + 1).padStart(2, "0");
          return (
            <li className={`${prefix}-entry`} key={index}>
              <div className={`${prefix}-entry-head`}>
                <span className={`${prefix}-entry-index`} aria-hidden="true">{badge}</span>
                <h3 className={`${prefix}-entry-title`}>
                  {entry.href ? (
                    <a href={entry.href} rel="noreferrer">
                      <span><MarkdownInlineText nodes={entry.title} keyPrefix={`${prefix}-e-${index}`} /></span>
                      <ArrowUpRight size={14} aria-hidden="true" />
                    </a>
                  ) : (
                    <span><MarkdownInline nodes={entry.title} keyPrefix={`${prefix}-e-${index}`} /></span>
                  )}
                </h3>
                {entryLeader && <i className={`${prefix}-entry-leader`} aria-hidden="true" />}
                {showEntryHrefPreview && entry.href && (
                  <span className={`${prefix}-entry-target`} aria-hidden="true">
                    <span className={`${prefix}-entry-arrow`}>{"->"}</span> {entry.href}
                  </span>
                )}
              </div>
              {entry.description.length > 0 && (
                <p className={`${prefix}-entry-desc`}>
                  <MarkdownInline nodes={entry.description} keyPrefix={`${prefix}-d-${index}`} />
                </p>
              )}
              {entry.extra.length > 0 && <MarkdownBody nodes={entry.extra} />}
            </li>
          );
        })}
      </ul>
      {layout.tail.length > 0 && <MarkdownBody nodes={layout.tail} className={`${prefix}-tail`} />}
    </div>
  );
}

export function ThemeFooter({ showAttribution, t }: { showAttribution: boolean; t: ThemeRendererProps["t"] }) {
  if (!showAttribution) return null;
  return <footer className="v2-theme-footer"><ThemeAttribution t={t} /></footer>;
}

export function ThemeAttribution({ t }: { t: ThemeRendererProps["t"] }) {
  const brand = "30s.to";
  const label = t("theme.footer");
  const brandIndex = label.indexOf(brand);

  if (brandIndex === -1) {
    return <>{label} <ThemeBrandLink /></>;
  }

  return (
    <>
      {label.slice(0, brandIndex)}
      <ThemeBrandLink />
      {label.slice(brandIndex + brand.length)}
    </>
  );
}

export function ThemeBrandLink() {
  return (
    <a className="theme-brand-link" href="https://30s.to" rel="noreferrer" target="_blank">
      30s.to
    </a>
  );
}
